"use client";

// Отметка присутствия в портале. Шлётся раз в минуту и ТОЛЬКО когда человек
// реально смотрит: вкладка на экране и за последние пару минут были действия.
//
// Почему не по запросам API: портал сам опрашивает сервер раз в 30–60 секунд
// (уведомления, счётчики), поэтому забытая открытая вкладка выглядела бы как
// рабочий день. Считать так — значит завысить время в разы и получить цифру,
// по которой нельзя делать выводы.

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { apiRequest } from "@/lib/api";

const PING_MS = 60_000;      // как часто отмечаемся
const IDLE_MS = 2 * 60_000;  // столько тишины — и человек считается отошедшим

export function usePresence(enabled: boolean) {
  const pathname = usePathname();
  const lastActivity = useRef(Date.now());
  // Путь в ref, чтобы смена страницы не пересоздавала таймер и не сбивала такт.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (!enabled) return;
    const touch = () => { lastActivity.current = Date.now(); };
    const events: (keyof WindowEventMap)[] = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "focus"];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));

    const send = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastActivity.current > IDLE_MS) return;
      // Ошибку глотаем: присутствие — вспомогательная телеметрия, из-за неё
      // не должно всплывать ничего в интерфейсе.
      void apiRequest("/presence/ping", {
        method: "POST",
        body: JSON.stringify({ path: pathRef.current || "/" }),
      }).catch(() => {});
    };

    send();
    const timer = setInterval(send, PING_MS);
    // Вернулся на вкладку — отмечаемся сразу, не дожидаясь такта.
    const onVisible = () => { if (document.visibilityState === "visible") { touch(); send(); } };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      events.forEach((e) => window.removeEventListener(e, touch));
    };
  }, [enabled]);
}
